import {
	Alert,
	Autocomplete,
	Box,
	Button,
	ButtonBase,
	Checkbox,
	Collapse,
	FormControlLabel,
	InputAdornment,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { HomeownerMortgage } from "../../interfaces/AppDefinitions";
import { INK, INK_2, LINE, MUTED } from "../modern/shared";
import { LENDERS } from "./lenders";

export interface MortgageFormValues {
	lender: string | null;
	balance: number;
	rate: number;
	rateType: "fixed" | "variable";
	termEnd: string | null; // "YYYY-MM"
	payment: number | null;
	yearsLeft: number | null;
	consent: boolean;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const YEARS_LEFT = [5, 10, 15, 20, 25, 30, 35];

// "545,000" -> 545000; "" -> null
function toNumber(text: string) {
	const cleaned = text.replace(/[^0-9.]/g, "");
	if (!cleaned) return null;
	const n = Number(cleaned);
	return isFinite(n) ? n : null;
}
function withCommas(text: string) {
	const n = toNumber(text);
	return n === null ? "" : Math.round(n).toLocaleString("en-US");
}

// The three steps: lender and balance, rate and renewal, then consent. Every field but the balance,
// rate and consent has a "Not sure" way out.
export default function MortgageForm(props: {
	initial: HomeownerMortgage | null;
	agentName?: string;
	cancelLabel: string;
	onCancel: () => void;
	onSubmit: (values: MortgageFormValues) => Promise<void>;
	compact?: boolean; // inside the equity panel instead of a dialog
}) {
	const { appState } = useContext(AppContext);
	const brand = appState.colorPallete?.["scheme1"] || "#1f5fae";
	const initial = props.initial;
	const now = new Date();
	const thisYear = now.getFullYear();
	const thisMonth = String(now.getMonth() + 1).padStart(2, "0");
	// A term that has already ended has been renewed, so we ask for the new end date instead.
	const currentTermEnd = initial?.termEnd && !initial.termEnded ? initial.termEnd : "";
	const [step, setStep] = useState(1);
	const [lender, setLender] = useState(initial?.lender ?? "");
	const [balance, setBalance] = useState(initial ? Math.round(initial.balanceToday).toLocaleString("en-US") : "");
	const [rate, setRate] = useState(initial ? String(initial.rate) : "");
	const [rateType, setRateType] = useState<"fixed" | "variable">(initial?.rateType ?? "fixed");
	const [termMonth, setTermMonth] = useState(currentTermEnd ? currentTermEnd.slice(5, 7) : "");
	const [termYear, setTermYear] = useState(currentTermEnd ? currentTermEnd.slice(0, 4) : "");
	const [more, setMore] = useState(false);
	const [payment, setPayment] = useState(
		initial?.paymentEntered ? Math.round(initial.payment).toLocaleString("en-US") : "",
	);
	const [yearsLeft, setYearsLeft] = useState(initial ? String(initial.amortizationYears) : "");
	const [consent, setConsent] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const agent = props.agentName || "your agent";

	const next = () => {
		setError(null);
		if (step === 1) {
			const b = toNumber(balance);
			if (b === null || b < 1000) return setError("Enter about how much is left on your mortgage.");
			setStep(2);
		} else if (step === 2) {
			const r = toNumber(rate);
			if (r === null || r <= 0 || r > 25) return setError("Enter your interest rate, for example 4.89.");
			if ((termMonth && !termYear) || (!termMonth && termYear && termYear !== "unsure"))
				return setError("Pick both the month and the year your term ends, or choose Not sure.");
			if (
				termMonth &&
				termYear &&
				termYear !== "unsure" &&
				`${termYear}-${termMonth}` < `${thisYear}-${thisMonth}`
			)
				return setError("Pick this month or later. If your term has ended, enter when your new term ends.");
			setStep(3);
		}
	};

	const back = () => {
		setError(null);
		setStep(step - 1);
	};

	const submit = async () => {
		setError(null);
		if (!consent) return setError(`Tick the box to share these details with ${agent}.`);
		setSaving(true);
		try {
			await props.onSubmit({
				lender: lender.trim() || null,
				balance: toNumber(balance) ?? 0,
				rate: toNumber(rate) ?? 0,
				rateType,
				termEnd: termMonth && termYear && termYear !== "unsure" ? `${termYear}-${termMonth}` : null,
				payment: toNumber(payment),
				yearsLeft: yearsLeft ? Number(yearsLeft) : null,
				consent: true,
			});
		} catch (err: any) {
			setError(err?.message || "We couldn't save that. Please try again.");
			setSaving(false);
		}
	};

	const titles = [
		initial ? "Update your mortgage" : "See how much of your home you own",
		"Your rate and renewal",
		"Almost done",
	];

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "14px", color: INK }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					pr: props.compact ? 0 : 4,
				}}
			>
				<Box sx={{ display: "flex", gap: "4px" }} aria-hidden="true">
					{[1, 2, 3].map((n) => (
						<Box
							key={n}
							sx={{ width: 22, height: 4, borderRadius: 9, backgroundColor: n <= step ? brand : LINE }}
						/>
					))}
				</Box>
				<Typography sx={{ fontSize: "12px", color: MUTED }}>{step} of 3</Typography>
			</Box>
			<Box>
				<Typography
					id="mortgage-form-title"
					component="h2"
					sx={{ fontSize: props.compact ? "18px" : "20px", fontWeight: 700, lineHeight: 1.25 }}
				>
					{titles[step - 1]}
				</Typography>
				{step === 1 && (
					<Typography sx={{ fontSize: "13px", color: MUTED, mt: "4px" }}>
						Two quick questions. {agent} uses this to let you know when a renewal or a better rate comes up.
					</Typography>
				)}
			</Box>

			{step === 1 && (
				<>
					<Autocomplete
						freeSolo
						options={LENDERS}
						value={lender}
						onInputChange={(_e, v) => setLender(v)}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Who holds your mortgage?"
								placeholder="Pick or type your lender"
								size="small"
							/>
						)}
					/>
					<TextField
						label="About how much is left?"
						value={balance}
						onChange={(e) => setBalance(e.target.value)}
						onBlur={() => setBalance(withCommas(balance))}
						size="small"
						autoFocus={!props.compact}
						inputProps={{ inputMode: "numeric", "aria-describedby": "balance-hint" }}
						InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
						helperText={<span id="balance-hint">A rough number is fine.</span>}
					/>
				</>
			)}

			{step === 2 && (
				<>
					<Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "10px" }}>
						<TextField
							label="Interest rate"
							value={rate}
							onChange={(e) => setRate(e.target.value)}
							size="small"
							autoFocus={!props.compact}
							inputProps={{ inputMode: "decimal" }}
							InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
						/>
						<ToggleButtonGroup
							exclusive
							size="small"
							value={rateType}
							onChange={(_e, v) => v && setRateType(v)}
							aria-label="Rate type"
						>
							<ToggleButton value="fixed" sx={{ textTransform: "none", px: "14px" }}>
								Fixed
							</ToggleButton>
							<ToggleButton value="variable" sx={{ textTransform: "none", px: "14px" }}>
								Variable
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
					<Box>
						<Typography sx={{ fontSize: "13px", fontWeight: 600, color: INK_2, mb: "6px" }}>
							When does your term end?
						</Typography>
						{initial?.termEnded && (
							<Typography sx={{ fontSize: "12px", color: MUTED, mt: "-2px", mb: "6px" }}>
								Your last term ended, so enter when your new one ends.
							</Typography>
						)}
						<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
							<TextField
								select
								size="small"
								label="Month"
								value={termMonth}
								onChange={(e) => setTermMonth(e.target.value)}
								disabled={termYear === "unsure"}
							>
								<MenuItem value="">-</MenuItem>
								{MONTHS.map((m, i) => {
									const value = String(i + 1).padStart(2, "0");
									return (
										<MenuItem
											key={m}
											value={value}
											disabled={termYear === String(thisYear) && value < thisMonth}
										>
											{m}
										</MenuItem>
									);
								})}
							</TextField>
							<TextField
								select
								size="small"
								label="Year"
								value={termYear}
								onChange={(e) => {
									const year = e.target.value;
									setTermYear(year);
									// Clear the month if it is now in the past (or if they're not sure).
									if (
										year === "unsure" ||
										(year === String(thisYear) && termMonth && termMonth < thisMonth)
									)
										setTermMonth("");
								}}
							>
								<MenuItem value="">-</MenuItem>
								<MenuItem value="unsure">Not sure</MenuItem>
								{Array.from({ length: 11 }, (_, i) => thisYear + i).map((y) => (
									<MenuItem key={y} value={String(y)}>
										{y}
									</MenuItem>
								))}
							</TextField>
						</Box>
					</Box>
					<ButtonBase
						onClick={() => setMore(!more)}
						aria-expanded={more}
						sx={{ alignSelf: "flex-start", fontSize: "13px", fontWeight: 600, color: brand }}
					>
						{more ? "Hide extra details" : "Add your payment and years left (optional)"}
					</ButtonBase>
					<Collapse in={more}>
						<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", pt: "2px" }}>
							<TextField
								label="Monthly payment"
								value={payment}
								onChange={(e) => setPayment(e.target.value)}
								onBlur={() => setPayment(withCommas(payment))}
								size="small"
								inputProps={{ inputMode: "numeric" }}
								InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
							/>
							<TextField
								select
								size="small"
								label="Years left to pay"
								value={yearsLeft}
								onChange={(e) => setYearsLeft(e.target.value)}
							>
								<MenuItem value="">Not sure</MenuItem>
								{YEARS_LEFT.map((y) => (
									<MenuItem key={y} value={String(y)}>
										{y} years
									</MenuItem>
								))}
							</TextField>
						</Box>
					</Collapse>
				</>
			)}

			{step === 3 && (
				<>
					<FormControlLabel
						sx={{ alignItems: "flex-start", m: 0, gap: "4px" }}
						control={
							<Checkbox
								checked={consent}
								onChange={(e) => setConsent(e.target.checked)}
								sx={{ p: "2px 6px 0 0", color: MUTED, "&.Mui-checked": { color: brand } }}
							/>
						}
						label={
							<Typography sx={{ fontSize: "14px", color: INK_2, lineHeight: 1.45 }}>
								Share these details with {agent} so they can show my equity and tell me about renewal
								and refinance options.
							</Typography>
						}
					/>
					<Typography sx={{ fontSize: "12px", color: MUTED }}>
						Your details stay private to you and {agent}. You can change them any time.
					</Typography>
				</>
			)}

			{error && (
				<Alert severity="warning" sx={{ py: 0 }}>
					{error}
				</Alert>
			)}

			<Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
				<Button
					variant="contained"
					disableElevation
					onClick={step === 3 ? submit : next}
					disabled={saving}
					sx={{
						backgroundColor: brand,
						textTransform: "none",
						fontWeight: 700,
						fontSize: "15px",
						borderRadius: "11px",
						py: "9px",
						"&:hover": { backgroundColor: brand, filter: "brightness(1.08)" },
					}}
				>
					{step === 3 ? (saving ? "Saving…" : "Show my equity") : "Next"}
				</Button>
				<Button
					onClick={step === 1 ? props.onCancel : back}
					disabled={saving}
					sx={{ textTransform: "none", color: MUTED, fontWeight: 600 }}
				>
					{step === 1 ? props.cancelLabel : "Back"}
				</Button>
			</Box>
		</Box>
	);
}
